import React, { useState, useEffect } from "react";

import { useTranslation } from "react-i18next";
import ErrorBanner from "../components/ui/ErrorBanner";
import SkeletonList from "../components/ui/SkeletonList";

import { IoMdArrowBack } from "react-icons/io";
import { IoClose } from "react-icons/io5";
import { ChevronRight } from "lucide-react";

import { useNavigate } from "react-router-dom";
import { useLoading } from "../context/LoadingContext";
import { fetchDepositChannelList } from "../utils/api";
import { assetPath } from "../utils/assets";
import FloatingMenu from "../components/FloatingMenu";

const PAYMENT_METHODS = [
    { id: 21, name: "Online Transfer" },
    { id: 24, name: "Payment Gateway" },
    { id: 26, name: "Crypto" },
];

const PAYMENT_ROUTES = {
    "Online Transfer": "/bank-transfer-deposit",
    "Payment Gateway": "/payment-gateway-deposit",
    Crypto: "/crypto-deposit",
};

const PAYMENT_ICONS = {
    "Online Transfer": assetPath("/icons/bank.png"),
    Crypto: assetPath("/icons/crypto.png"),
    default: assetPath("/icons/card.png"),
};

const Deposit = () => {
    const { t } = useTranslation();
    const { setLoading } = useLoading();
    const navigate = useNavigate();

    const [errorMessage, setErrorMessage] = useState("");

    const [paymentMethodsLoading, setPaymentMethodsLoading] = useState(true);
    const [paymentMethodList, setPaymentMethodList] = useState([]);

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            setPaymentMethodsLoading(true);
            setErrorMessage("");

            try {
                const response = await fetchDepositChannelList();

                if (response.success && Array.isArray(response.data) && response.data.length > 0) {
                    setPaymentMethodList(response.data);
                } else {
                    setPaymentMethodList([]);
                }
            } catch (error) {
                setErrorMessage(t("pleaseTryLater"));
            } finally {
                setLoading(false);
                setPaymentMethodsLoading(false);
            }
        };

        loadData();
    }, [setLoading, t]);

    useEffect(() => {
        if (errorMessage) {
            window.scrollTo({ top: 0, behavior: "smooth" });
        }
    }, [errorMessage]);

    const handlePaymentMethodClick = (method) => {
        const route = PAYMENT_ROUTES[method.name];
        if (route) {
            navigate(route);
        }
    };

    return (
        <section className="page-shell">
            <div className="page-header">
                <div
                    className="cursor-pointer bg-secondary container-back-button text-color-blue rounded-full"
                    onClick={() => navigate("/")}
                >
                    <IoMdArrowBack className="size-sm" />
                    <span className="text-sm">
                        {t("backToLobby")}
                    </span>
                </div>
                <button className="cursor-pointer" onClick={() => navigate("/")}>
                    <IoClose className="size-lg text-color-blue" />
                </button>
            </div>

            <ErrorBanner message={errorMessage} />

            <div className="px-4">
                <p className="page-subtitle-centered">
                    {t("chooseDepositMethod")}
                </p>

                {paymentMethodsLoading ? (
                    <SkeletonList
                        rows={PAYMENT_METHODS.length}
                        height={40}
                        className="payment-method-list"
                    />
                ) : (
                    <div className="payment-method-list">
                        {paymentMethodList.length > 0 ? (
                            paymentMethodList.map((method) => (
                                <div
                                    key={method.id}
                                    className="payment-method-item"
                                    onClick={() => handlePaymentMethodClick(method)}
                                >
                                    <div className="flex-center gap-2">
                                        <img
                                            src={PAYMENT_ICONS[method.name] || PAYMENT_ICONS.default}
                                            alt={`${method.name} icon`}
                                            className="size-md"
                                        />
                                        <span className="text-sm font-bold">
                                            {t(method.name)}
                                        </span>
                                    </div>
                                    <ChevronRight className="size-sm" />
                                </div>
                            ))
                        ) : (
                            <div className="empty-state">
                                {t("noDepositChannelsAvailable")}
                            </div>
                        )}
                    </div>
                )}
            </div>

            <FloatingMenu />
        </section>
    );
};

export default Deposit;